import React, { useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import {
  UserPlus,
  Briefcase,
  UserCog,
  Users,
  Shield,
  Activity,
  Cog,
  Bot,
  Package,
  TestTube,
  BarChart3,
  Settings,
  Database,
  FileText,
  Monitor,
  TrendingUp,
  Eye,
  Lock,
  CreditCard,
} from "lucide-react";

export interface MenuItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick: () => void;
  permission?: string;
  category?: string;
}

export interface ProfileMenuConfig {
  profileId?: string; // Opcional, usado apenas para compatibilidade
  profileName: string; // Nome é a chave principal
  menus: {
    [key: string]: {
      label: string;
      icon: React.ComponentType<{ className?: string }>;
      items: MenuItem[];
    };
  };
}

// Configuração de menus por perfil (apenas perfis reais do banco)
export const profileMenuConfigs: ProfileMenuConfig[] = [
  {
    profileId: "550e8400-e29b-41d4-a716-446655440001",
    profileName: "Administrador",
    menus: {
      management: {
        label: "Gerenciamento",
        icon: Users,
        items: [
          {
            id: "cadastrar-cliente",
            label: "Cadastrar Cliente",
            icon: UserPlus,
            onClick: () => {},
            permission: "manage_clients",
            category: "clientes",
          },
          {
            id: "cadastrar-workspace",
            label: "Cadastrar Workspace",
            icon: Briefcase,
            onClick: () => {},
            permission: "manage_workspaces",
            category: "workspaces",
          },
          {
            id: "gerenciar-perfis",
            label: "Gerenciar Perfis",
            icon: UserCog,
            onClick: () => {},
            permission: "manage_profiles",
            category: "perfis",
          },
          {
            id: "gerenciar-usuarios",
            label: "Gerenciar Usuários",
            icon: Users,
            onClick: () => {},
            permission: "manage_users",
            category: "usuarios",
          },
          {
            id: "api-cliente",
            label: "API Cliente",
            icon: FileText,
            onClick: () => {},
            permission: "manage_client_api",
            category: "api",
          },
        ],
      },
      security: {
        label: "Segurança",
        icon: Shield,
        items: [
          {
            id: "validacao-acesso",
            label: "Validação de Acesso",
            icon: Lock,
            onClick: () => {},
            permission: "access_validation",
            category: "seguranca",
          },
          {
            id: "auditoria",
            label: "Auditoria",
            icon: Activity,
            onClick: () => {},
            permission: "view_audit",
            category: "auditoria",
          },
          {
            id: "chamadas-api-seguranca",
            label: "Chamadas API",
            icon: Monitor,
            onClick: () => {},
            permission: "view_api_calls",
            category: "seguranca",
          },
        ],
      },
      system: {
        label: "Sistema",
        icon: Settings,
        items: [
          {
            id: "provedores-ia",
            label: "Provedores IA",
            icon: Database,
            onClick: () => {},
            permission: "manage_providers",
            category: "ia",
          },
          {
            id: "modelos-ia",
            label: "Modelos de IA",
            icon: Cog,
            onClick: () => {},
            permission: "manage_models",
            category: "ia",
          },
          {
            id: "agentes",
            label: "Agentes",
            icon: Bot,
            onClick: () => {},
            permission: "manage_agents",
            category: "ia",
          },
          {
            id: "especialistas",
            label: "Especialistas",
            icon: Package,
            onClick: () => {},
            permission: "manage_products",
            category: "especialistas",
          },
          {
            id: "planos",
            label: "Planos",
            icon: CreditCard,
            onClick: () => {},
            permission: "manage_plans",
            category: "planos",
          },
          {
            id: "sandbox",
            label: "Sandbox",
            icon: TestTube,
            onClick: () => {},
            permission: "view_sandbox",
            category: "desenvolvimento",
          },
        ],
      },
      analytics: {
        label: "Análises",
        icon: BarChart3,
        items: [
          {
            id: "relatorios",
            label: "Relatórios e Análises",
            icon: BarChart3,
            onClick: () => {},
            permission: "view_reports",
            category: "relatorios",
          },
        ],
      },
    },
  },
  {
    profileId: "550e8400-e29b-41d4-a716-446655440002",
    profileName: "Analista",
    menus: {
      analytics: {
        label: "Análises",
        icon: BarChart3,
        items: [
          {
            id: "relatorios",
            label: "Relatórios e Análises",
            icon: BarChart3,
            onClick: () => {},
            permission: "view_reports",
            category: "relatorios",
          },
          {
            id: "dashboard",
            label: "Dashboard",
            icon: TrendingUp,
            onClick: () => {},
            permission: "view_dashboard",
            category: "dashboard",
          },
        ],
      },
    },
  },
  {
    profileId: "550e8400-e29b-41d4-a716-446655440003",
    profileName: "Cliente",
    menus: {
      workspace: {
        label: "Workspace",
        icon: Briefcase,
        items: [
          {
            id: "dashboard-cliente",
            label: "Dashboard",
            icon: TrendingUp,
            onClick: () => {},
            permission: "view_own_data",
            category: "dashboard",
          },
          {
            id: "estatisticas-basicas",
            label: "Estatísticas",
            icon: BarChart3,
            onClick: () => {},
            permission: "view_basic_stats",
            category: "estatisticas",
          },
        ],
      },
    },
  },
  {
    profileId: "550e8400-e29b-41d4-a716-446655440004",
    profileName: "Suporte",
    menus: {
      support: {
        label: "Suporte",
        icon: Shield,
        items: [
          {
            id: "visualizar-usuarios",
            label: "Visualizar Usuários",
            icon: Users,
            onClick: () => {},
            permission: "view_users",
            category: "usuarios",
          },
          {
            id: "visualizar-logs",
            label: "Visualizar Logs",
            icon: FileText,
            onClick: () => {},
            permission: "view_logs",
            category: "logs",
          },
        ],
      },
    },
  },
  {
    profileId: "550e8400-e29b-41d4-a716-446655440005",
    profileName: "Admin Cliente",
    menus: {
      management: {
        label: "Gerenciamento",
        icon: Users,
        items: [
          {
            id: "visualizar-cliente",
            label: "Visualizar Cliente",
            icon: Briefcase,
            onClick: () => {},
            permission: "manage_clients",
            category: "clientes",
          },
          {
            id: "cadastrar-workspace",
            label: "Cadastrar Workspace",
            icon: Briefcase,
            onClick: () => {},
            permission: "manage_workspaces",
            category: "workspaces",
          },
          {
            id: "gerenciar-usuarios",
            label: "Gerenciar Usuários",
            icon: Users,
            onClick: () => {},
            permission: "manage_users",
            category: "usuarios",
          },
        ],
      },
      workspace: {
        label: "Workspace",
        icon: Briefcase,
        items: [
          {
            id: "gerenciar-workspace",
            label: "Gerenciar Workspaces",
            icon: Briefcase,
            onClick: () => {},
            permission: "manage_workspaces",
            category: "workspaces",
          },
          {
            id: "visualizar-usuarios",
            label: "Visualizar Usuários",
            icon: Users,
            onClick: () => {},
            permission: "view_users",
            category: "usuarios",
          },
        ],
      },
      analytics: {
        label: "Análises",
        icon: BarChart3,
        items: [
          {
            id: "dashboard",
            label: "Dashboard",
            icon: TrendingUp,
            onClick: () => {},
            permission: "view_dashboard",
            category: "dashboard",
          },
          {
            id: "relatorios",
            label: "Relatórios",
            icon: BarChart3,
            onClick: () => {},
            permission: "view_reports",
            category: "relatorios",
          },
        ],
      },
    },
  },
  {
    profileId: "550e8400-e29b-41d4-a716-446655440006",
    profileName: "Usuário Workspace",
    menus: {
      workspace: {
        label: "Meu Workspace",
        icon: Briefcase,
        items: [
          {
            id: "dashboard-pessoal",
            label: "Dashboard",
            icon: TrendingUp,
            onClick: () => {},
            permission: "view_dashboard",
            category: "dashboard",
          },
          {
            id: "meus-dados",
            label: "Meus Dados",
            icon: Eye,
            onClick: () => {},
            permission: "view_own_data",
            category: "dados",
          },
          {
            id: "estatisticas-basicas",
            label: "Estatísticas",
            icon: BarChart3,
            onClick: () => {},
            permission: "view_basic_stats",
            category: "estatisticas",
          },
        ],
      },
    },
  },
];

interface ProfileBasedMenuProps {
  profileId: string;
  profileName?: string;
  onMenuItemClick: (itemId: string, category: string) => void;
}

// Mapeamento de permissões antigas para novas hierárquicas
const permissionMapping: { [key: string]: string[] } = {
  // Permissões de gerenciamento - qualquer ação CRUD do recurso
  manage_clients: [
    "res_clients.create",
    "res_clients.read",
    "res_clients.update",
    "res_clients.delete",
    "admin.full",
  ],
  manage_users: [
    "res_users.create",
    "res_users.read",
    "res_users.update",
    "res_users.delete",
    "admin.full",
  ],
  manage_workspaces: [
    "res_workspaces.create",
    "res_workspaces.read",
    "res_workspaces.update",
    "res_workspaces.delete",
    "admin.full",
  ],
  manage_profiles: [
    "res_profiles.create",
    "res_profiles.read",
    "res_profiles.update",
    "res_profiles.delete",
    "admin.full",
  ],
  manage_agents: [
    "res_agents.create",
    "res_agents.read",
    "res_agents.update",
    "res_agents.delete",
    "admin.full",
  ],
  manage_products: [
    "res_products.create",
    "res_products.read",
    "res_products.update",
    "res_products.delete",
    "admin.full",
  ],
  manage_plans: [
    "res_plans.create",
    "res_plans.read",
    "res_plans.update",
    "res_plans.delete",
    "admin.full",
  ],
  manage_models: [
    "res_models.create",
    "res_models.read",
    "res_models.update",
    "res_models.delete",
    "admin.full",
  ],
  manage_providers: [
    "res_providers.create",
    "res_providers.read",
    "res_providers.update",
    "res_providers.delete",
    "admin.full",
  ],
  manage_client_api: [
    "res_client_api.create",
    "res_client_api.read",
    "res_client_api.update",
    "res_client_api.delete",
    "admin.full",
  ],

  // Permissões de visualização
  view_dashboard: ["res_dashboard.read", "admin.full"], // Opção global - todos têm acesso por padrão
  view_reports: ["res_reports.read", "admin.full"],
  view_api_calls: [
    "res_api_calls.read",
    "res_api_calls_security.read",
    "admin.full",
  ],
  view_logs: ["res_logs.read", "admin.full"],
  view_audit: ["res_audit.read", "admin.full"],
  view_users: ["res_users.read", "admin.full"],
  view_sandbox: ["res_sandbox.read", "admin.full"],

  // Permissões especiais
  view_own_data: [
    "res_workspace_data.view_own",
    "res_my_workspace.access",
    "admin.full",
  ],
  view_basic_stats: ["res_dashboard.read", "admin.full"],
  access_validation: ["res_access_control.validate", "admin.full"],
};

export const ProfileBasedMenu: React.FC<ProfileBasedMenuProps> = ({
  profileId,
  profileName,
  onMenuItemClick,
}) => {
  // TODOS OS HOOKS DEVEM SER CHAMADOS ANTES DE QUALQUER RETURN CONDICIONAL
  const { hasPermission, hasAnyPermission, isAdmin, isAdminOrSupport, permissions } =
    usePermissions();

  // Buscar configuração por nome (prioridade) ou por ID (fallback)
  const profileConfig = profileMenuConfigs.find(
    (config) =>
      (profileName && config.profileName === profileName) ||
      config.profileId === profileId
  );

  // Função para verificar se o usuário tem a permissão (com mapeamento)
  const checkPermission = (oldPermission: string): boolean => {
    // Se não tem permissão definida, permitir
    if (!oldPermission) return true;

    // Admin tem acesso a tudo
    if (isAdmin || hasPermission("admin.full")) return true;

    // Verificar se há mapeamento para a permissão antiga
    const mappedPermissions = permissionMapping[oldPermission];

    if (mappedPermissions) {
      // Verificar se tem qualquer uma das permissões mapeadas
      return hasAnyPermission(mappedPermissions);
    }

    // Verificar permissão diretamente (caso já seja nova nomenclatura)
    return hasPermission(oldPermission);
  };

  // Filtrar menus baseado nas permissões reais do usuário (só se profileConfig existir)
  const filteredMenus = profileConfig
    ? Object.entries(profileConfig.menus)
        .map(([menuKey, menu]) => {
          // Filtrar itens do menu baseado nas permissões
          const filteredItems = menu.items.filter((item) => {
            // Sandbox só para Administrador e Suporte
            if (item.id === "sandbox") {
              return isAdminOrSupport;
            }
            // Usar função de verificação com mapeamento
            return checkPermission(item.permission || "");
          });

          return {
            menuKey,
            menu,
            filteredItems,
          };
        })
        .filter((menuData) => menuData.filteredItems.length > 0)
    : []; // Só mostrar menus que têm pelo menos 1 item

  // Debug: Log de permissões e filtros (hook deve estar antes de qualquer return)
  useEffect(() => {
    if (profileConfig) {
    }
  }, [
    profileName,
    isAdmin,
    permissions.length,
    filteredMenus.length,
    profileConfig,
  ]);

  // Agora podemos fazer returns condicionais APÓS todos os hooks
  if (!profileConfig) {
    return null;
  }

  const handleMenuItemClick = (item: MenuItem) => {
    item.onClick();
    onMenuItemClick(item.id, item.category || "");
  };

  // Se não há menus para mostrar, não renderizar nada
  if (filteredMenus.length === 0) {
    // Se é admin mas não tem menus, é problema de configuração
    if (isAdmin || hasPermission("admin.full")) {
    }
    return null;
  }

  return (
    <div className="flex space-x-2">
      {filteredMenus.map(({ menuKey, menu, filteredItems }) => {
        const MenuIcon = menu.icon;
        return (
          <DropdownMenu key={menuKey}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="hover:bg-primary/10"
                title={menu.label}
              >
                <MenuIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56 bg-popover border shadow-lg z-50"
            >
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                {menu.label}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {filteredItems.map((item) => {
                const ItemIcon = item.icon;
                return (
                  <DropdownMenuItem
                    key={item.id}
                    onClick={() => handleMenuItemClick(item)}
                    className="cursor-pointer"
                  >
                    <ItemIcon className="h-4 w-4 mr-2" />
                    {item.label}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      })}
    </div>
  );
};

// Hook para gerenciar o perfil atual do usuário
export const useUserProfile = () => {
  const { user, updateUserPermissions, updateUserProfile } = useAuth();

  // Usar o perfil do usuário autenticado como padrão
  const [currentProfile, setCurrentProfile] = React.useState<string>(
    user?.profile_id || profileMenuConfigs[0]?.profileId || ""
  );
  const [isChanging, setIsChanging] = React.useState(false);

  // Atualizar currentProfile quando o user.profile_id mudar
  React.useEffect(() => {
    if (user?.profile_id) {
      setCurrentProfile(user.profile_id);
    }
  }, [user?.profile_id]);

  const changeProfile = async (profileId: string) => {
    // Em produção, não permitir mudança de perfil
    if (import.meta.env.PROD) {
      return;
    }

    setIsChanging(true);

    try {
      // Buscar as permissões do novo perfil
      const { getProfileById } = await import("@/services/profilesService");
      const response = await getProfileById(profileId);

      if (response.success && response.data) {
        const profile = response.data;

        // Atualizar o perfil e permissões no contexto
        updateUserProfile(profile.id, profile.name);
        updateUserPermissions(profile.permissions || []);

        // Atualizar estado local
        setCurrentProfile(profileId);

      } else {
      }
    } catch (error) {
    } finally {
      setIsChanging(false);
    }
  };

  return {
    currentProfile,
    changeProfile,
    isChanging,
    availableProfiles: profileMenuConfigs.map((config) => ({
      id: config.profileId,
      name: config.profileName,
    })),
  };
};
